import React, { useEffect, useRef, useState } from 'react';
import { Business } from '../types';
import { GOOGLE_MAPS_CONFIG, isGoogleMapsConfigured } from '../config/googleMaps';
import {
  RotateCcw,
  ZoomIn,
  ZoomOut,
  Navigation,
  MapPin,
  AlertCircle,
  Eye,
  Move,
  RotateCw,
  Maximize2,
  Minimize2
} from 'lucide-react';

interface StreetViewComponentProps {
  business: Business;
  height?: string;
  onStreetViewLoad?: (available: boolean) => void;
}

const StreetViewComponent: React.FC<StreetViewComponentProps> = ({
  business,
  height = '400px',
  onStreetViewLoad
}) => {
  const streetViewRef = useRef<HTMLDivElement | null>(null);
  const [streetView, setStreetView] = useState<google.maps.StreetViewPanorama | null>(null);
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [heading, setHeading] = useState(0);
  const [pitch, setPitch] = useState(0);
  const [zoom, setZoom] = useState(1);

  // Initialize Street View
  useEffect(() => {

    if (!streetViewRef.current || !isGoogleMapsConfigured()) {
      console.warn("StreetView ref not ready yet.");
      return;
    }

    const initializeStreetView = async () => {
      try {
        setIsLoading(false);

        // Create Street View service to check availability
        const streetViewService = new google.maps.StreetViewService();
        const position = { lat: business.latitude, lng: business.longitude };


        // Check if Street View is available at this location
        streetViewService.getPanorama({
          location: position,
          radius: 50, // Search within 50 meters
          source: google.maps.StreetViewSource.OUTDOOR
        }, (data, status) => {
          const available = status === google.maps.StreetViewStatus.OK;
          setIsAvailable(available);
          onStreetViewLoad?.(available);

          console.log('Street View availability status:', available, status, data);

          if (available && data) {
            // Create Street View panorama
            const panorama = new google.maps.StreetViewPanorama(
              streetViewRef.current!,
              {
                position: data.location?.latLng || position,
                pov: {
                  heading: 0,
                  pitch: 0
                },
                zoom: 1,
                addressControl: true,
                linksControl: true,
                panControl: true,
                enableCloseButton: false,
                fullscreenControl: false,
                motionTracking: true,
                motionTrackingControl: true,
                showRoadLabels: true
              }
            );

            // Listen for POV changes
            panorama.addListener('pov_changed', () => {
              const pov = panorama.getPov();
              setHeading(Math.round(pov.heading || 0));
              setPitch(Math.round(pov.pitch || 0));
            });

            // Listen for zoom changes
            panorama.addListener('zoom_changed', () => {
              setZoom(panorama.getZoom() || 1);
            });

            setStreetView(panorama);
          }

          setIsLoading(false);
        });
      } catch (error) {
        console.error('Street View initialization error:', error);
        setIsAvailable(false);
        setIsLoading(false);
        onStreetViewLoad?.(false);
      }
    };

    // Wait for Google Maps API to be loaded
    if (window.google && window.google.maps) {
      initializeStreetView();
    } else {
      // Wait for the API to load
      const checkGoogleMaps = setInterval(() => {
        if (window.google && window.google.maps) {
          clearInterval(checkGoogleMaps);
          initializeStreetView();
        }
      }, 100);

      return () => clearInterval(checkGoogleMaps);
    }
  }, [business.latitude, business.longitude, onStreetViewLoad]);

  // Street View controls
  const handleZoomIn = () => {
    if (streetView) {
      const currentZoom = streetView.getZoom() || 1;
      streetView.setZoom(Math.min(currentZoom + 1, 5));
    }
  };

  const handleZoomOut = () => {
    if (streetView) {
      const currentZoom = streetView.getZoom() || 1;
      streetView.setZoom(Math.max(currentZoom - 1, 0));
    }
  };

  const handleRotateLeft = () => {
    if (streetView) {
      const pov = streetView.getPov();
      streetView.setPov({
        ...pov,
        heading: (pov.heading || 0) - 30
      });
    }
  };

  const handleRotateRight = () => {
    if (streetView) {
      const pov = streetView.getPov();
      streetView.setPov({
        ...pov,
        heading: (pov.heading || 0) + 30
      });
    }
  };

  const handleResetView = () => {
    if (streetView) {
      streetView.setPov({
        heading: 0,
        pitch: 0
      });
      streetView.setZoom(1);
    }
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center bg-gray-100 rounded-lg" style={{ height }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
          <p className="text-sm text-gray-600">Loading Street View...</p>
        </div>
      </div>
    );
  }

  // Street View not available
  if (isAvailable === false) {
    return (
      <div className="flex items-center justify-center bg-gray-100 rounded-lg border-2 border-dashed border-gray-300" style={{ height }}>
        <div className="text-center p-6">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-700 mb-2">Street View Not Available</h3>
          <p className="text-sm text-gray-600 mb-4">
            Street View imagery is not available for this location.
          </p>
          <div className="flex items-center justify-center space-x-2 text-xs text-gray-500">
            <MapPin className="w-4 h-4" />
            <span>{business.address}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={isFullscreen ? 'fixed inset-0 bg-black' : 'relative rounded-lg overflow-hidden'}
      style={{ zIndex: isFullscreen ? 9999 : undefined }}
    >
      {/* Street View Container */}
      <div
        ref={streetViewRef}
        className="w-full"
        style={{ height: isFullscreen ? '100vh' : height || '300px' }}
      />

      {/* Fullscreen Exit Button - always on top, very prominent */}
      {isFullscreen && (
        <button
          onClick={toggleFullscreen}
          className="fixed top-4 left-1/2 -translate-x-1/2 z-[10000] bg-white text-gray-900 font-semibold px-5 py-2.5 rounded-full shadow-xl border border-gray-200 flex items-center gap-2 hover:bg-gray-100 active:scale-95 transition-all duration-150"
          style={{ zIndex: 10000 }}
          aria-label="Exit Street View"
        >
          <Minimize2 className="w-4 h-4" />
          <span>Exit Street View</span>
        </button>
      )}

      {/* Control Overlay */}
      <div
        className="absolute top-4 right-4"
        style={{ zIndex: isFullscreen ? 10001 : 10 }}
      >
        <div className="bg-white bg-opacity-90 backdrop-blur-sm rounded-lg p-2 shadow-lg">
          <div className="flex flex-col space-y-2">
            {/* Zoom Controls */}
            <div className="flex space-x-1">
              <button
                onClick={handleZoomIn}
                className="p-2 bg-white hover:bg-gray-50 rounded border border-gray-300 transition-colors duration-200"
                title="Zoom In"
              >
                <ZoomIn className="w-4 h-4 text-gray-700" />
              </button>
              <button
                onClick={handleZoomOut}
                className="p-2 bg-white hover:bg-gray-50 rounded border border-gray-300 transition-colors duration-200"
                title="Zoom Out"
              >
                <ZoomOut className="w-4 h-4 text-gray-700" />
              </button>
            </div>

            {/* Rotation Controls */}
            <div className="flex space-x-1">
              <button
                onClick={handleRotateLeft}
                className="p-2 bg-white hover:bg-gray-50 rounded border border-gray-300 transition-colors duration-200"
                title="Rotate Left"
              >
                <RotateCcw className="w-4 h-4 text-gray-700" />
              </button>
              <button
                onClick={handleRotateRight}
                className="p-2 bg-white hover:bg-gray-50 rounded border border-gray-300 transition-colors duration-200"
                title="Rotate Right"
              >
                <RotateCw className="w-4 h-4 text-gray-700" />
              </button>
            </div>

            {/* Reset and Fullscreen toggle */}
            <div className="flex space-x-1">
              <button
                onClick={handleResetView}
                className="p-2 bg-white hover:bg-gray-50 rounded border border-gray-300 transition-colors duration-200"
                title="Reset View"
              >
                <Navigation className="w-4 h-4 text-gray-700" />
              </button>
              <button
                onClick={toggleFullscreen}
                className="p-2 bg-white hover:bg-gray-50 rounded border border-gray-300 transition-colors duration-200"
                title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
              >
                {isFullscreen ? (
                  <Minimize2 className="w-4 h-4 text-gray-700" />
                ) : (
                  <Maximize2 className="w-4 h-4 text-gray-700" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Info Overlay */}
      <div
        className="absolute bottom-4 left-4"
        style={{ zIndex: isFullscreen ? 10001 : 10 }}
      >
        <div className="bg-black bg-opacity-70 text-white rounded-lg p-3 backdrop-blur-sm">
          <div className="flex items-center space-x-2 mb-2">
            <Eye className="w-4 h-4" />
            <span className="text-sm font-medium">{business.name}</span>
          </div>
          <div className="text-xs space-y-1">
            <div className="flex items-center space-x-2">
              <MapPin className="w-3 h-3" />
              <span>{business.address}</span>
            </div>
            <div className="flex items-center space-x-4 text-gray-300">
              <span>Heading: {heading}°</span>
              <span>Pitch: {pitch}°</span>
              <span>Zoom: {zoom}x</span>
            </div>
          </div>
        </div>
      </div>

      {/* Instructions (only in non-fullscreen mode to avoid clutter) */}
      {!isFullscreen && (
        <div className="absolute bottom-4 right-4 z-10">
          <div className="bg-white bg-opacity-90 backdrop-blur-sm rounded-lg p-2 shadow-lg">
            <div className="text-xs text-gray-600 space-y-1">
              <div className="flex items-center space-x-1">
                <Move className="w-3 h-3" />
                <span>Drag to look around</span>
              </div>
              <div className="flex items-center space-x-1">
                <span className="w-3 h-3 text-center text-xs">↕</span>
                <span>Scroll to zoom</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StreetViewComponent;



